import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
  HttpEvent,
} from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggerService } from '../../services/logger.service';
import { LocalStorageKeys } from '../../../utils/local-storage-keys';
import { ErrorDetail, SubError } from './dto/rest-response';
import { environment } from '../../../../../environments/environment';
import { LoadingService } from '../../loading/loading.service';

export interface OptionsHttpHelisa {
  headers?: HttpHeaders;
  observe?: 'body';
  params?: HttpParams;
  reportProgress?: boolean;
  withCredentials?: boolean;
  removeContentType?: boolean;
  hideLoading?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HttpHelisaClientService {
  private headers: HttpHeaders = new HttpHeaders();
  private httpClient: HttpClient = inject(HttpClient);
  private loadingService: LoadingService = inject(LoadingService);
  log = inject(LoggerService);

  constructor() {
    this.headers = this.headers.append(
      'Authorization',
      'Bearer ' +
        (localStorage.getItem(LocalStorageKeys.credentialsKey) || 'null')
    );
    this.headers = this.headers.append(
      'helisa-company',
      localStorage.getItem(LocalStorageKeys.helisaCompanyKey) || 'null'
    );
    this.headers = this.headers.append(
      'content-type',
      'application/json;charset=UTF-8'
    );
  }

  manageError(
    responseError: HttpErrorResponse,
    url: string = '',
    options: OptionsHttpHelisa,
    stopRedirectLogin?: boolean
  ): Array<string> {
    this.log.logError('Request error ' + JSON.stringify(responseError));
    if (responseError.status === 401 && !stopRedirectLogin) {
      window.location.href =
        environment.authFrontend + '/auth/login?expired=true';
    }
    const allError: Array<string> = [];
    const errorHelisa: ErrorDetail = responseError.error;
    if (!!errorHelisa) {
      if (errorHelisa.subErrors) {
        errorHelisa.subErrors.forEach((subError: SubError): void => {
          allError.push(subError.message as string);
          subError.errors?.forEach((error: SubError): void => {
            allError.push(error.message as string);
          });
        });
      } else if (errorHelisa.message) {
        allError.push(errorHelisa.message as string);
      }
      if (!options || !options.hideLoading) {
        this.loadingService.closeLoadingComponent(url);
      }
      this.loadingService.closeLoadingComponent(url);
      return allError;
    } else {
      allError.push('Error interno del servidor.');
    }
    if (!options || !options.hideLoading) {
      this.loadingService.closeLoadingComponent(url);
    }
    return allError;
  }

  post<R>(
    service: string,
    url: string,
    body: any,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }

    return this.httpClient
      .post<R>(environment.proxy, body, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  postEvent<R>(
    service: string,
    url: string,
    body: any,
    options?: OptionsHttpHelisa
  ): Observable<HttpEvent<R>> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .post<R>(environment.proxy, body, {
        ...this.getOptions(service, url, options),
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  postQuery<R>(
    service: string,
    url: string,
    body: string,
    options?: OptionsHttpHelisa
  ): Observable<R[]> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .post<
        R[]
      >(environment.proxy, body, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  // tslint:disable-next-line: no-any
  postMultipart<R>(
    service: string,
    url: string,
    body: any,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    const newOptions: OptionsHttpHelisa = this.getOptions(
      service,
      url,
      options
    );
    newOptions.headers = newOptions.headers?.delete('content-type');
    return this.httpClient.post<R>(environment.proxy, body, newOptions).pipe(
      // tslint:disable-next-line:typedef
      tap(() => {
        if (!options || !options.hideLoading) {
          this.loadingService.closeLoadingComponent(url);
        }
      }),
      // tslint:disable-next-line:typedef
      catchError((error: HttpErrorResponse) => {
        throw this.manageError(error, url, options as OptionsHttpHelisa);
      })
    );
  }

  // tslint:disable-next-line: no-any
  patchMultipart<R>(
    service: string,
    url: string,
    body: any,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    const newOptions: OptionsHttpHelisa = this.getOptions(
      service,
      url,
      options
    );
    newOptions.headers = newOptions.headers?.delete('content-type');
    return this.httpClient.patch<R>(environment.proxy, body, newOptions).pipe(
      // tslint:disable-next-line:typedef
      tap(() => {
        if (!options || !options.hideLoading) {
          this.loadingService.closeLoadingComponent(url);
        }
      }),
      // tslint:disable-next-line:typedef
      catchError((error: HttpErrorResponse) => {
        throw this.manageError(error, url, options as OptionsHttpHelisa);
      })
    );
  }

  patchQuery<R, T>(
    service: string,
    url: string,
    body: T,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .patch<R>(environment.proxy, body, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        // tslint:disable-next-line:typedef
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  patch<R>(
    service: string,
    url: string,
    body: R | null,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .patch<R>(environment.proxy, body, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  put<R>(
    service: string,
    url: string,
    body: any,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .put<R>(environment.proxy, body, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  delete<R>(
    service: string,
    url: string,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .delete<R>(environment.proxy, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  get<R>(
    service: string,
    url: string,
    options?: OptionsHttpHelisa
  ): Observable<R[]> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .get<R[]>(environment.proxy, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  getObject<R>(
    service: string,
    url: string,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .get<R>(environment.proxy, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  postQueryObject<R>(
    service: string,
    url: string,
    body: string,
    options?: OptionsHttpHelisa
  ): Observable<R> {
    if (!options || !options.hideLoading) {
      this.loadingService.showLoadingComponent(url);
    }
    return this.httpClient
      .post<R>(environment.proxy, body, this.getOptions(service, url, options))
      .pipe(
        // tslint:disable-next-line:typedef
        tap(() => {
          if (!options || !options.hideLoading) {
            this.loadingService.closeLoadingComponent(url);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw this.manageError(error, url, options as OptionsHttpHelisa);
        })
      );
  }

  private getOptions(
    service: string,
    url: string,
    options?: OptionsHttpHelisa | undefined
  ): OptionsHttpHelisa {
    if (!options || !options.headers) {
      if (!options) {
        options = {};
      }
      options.headers = this.headers;
      options.headers = options.headers.set(
        'Authorization',
        'Bearer ' +
          (localStorage.getItem(LocalStorageKeys.credentialsKey) || 'null')
      );
      options.headers = options.headers.set(
        'helisa-company',
        localStorage.getItem(LocalStorageKeys.helisaCompanyKey) || 'null'
      );
      options.headers = options.headers.set(
        'helisa-license',
        localStorage.getItem(LocalStorageKeys.authLicenseKey) || 'null'
      );
      options.headers = options.headers.set('helisa-url', url);
      options.headers = options.headers.set('helisa-service', service);
      if (options.removeContentType) {
        options.headers = options.headers.delete('content-type');
      }
    }
    return options;
  }
}
